/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect, useState } from "react";

import Head from "next/head";
import { useRouter } from "next/router";
import styled from "styled-components";

import { API } from "src/classes/api";
import { BaseUser } from "src/classes/user";
import { LoadingType, LoadingWrapper } from "src/components/Layout/LoadingWrapper";
import { PageWrapper, ScrollContainer } from "src/components/Layout/PageLayout";
import ProfilePill from "src/components/Profile/ProfilePill";
import { ProfileTabs } from "src/components/Profile/types";
import { UserInfo } from "src/components/Profile/UserInfo";
import UserList from "src/components/Profile/UserList";
import UserPlates from "src/components/Profile/UserPlates";
import { NoSSR } from "src/components/UI/NoSSR/NoSSR";
import { LoadingStatus } from "src/lib/types";

export default function Profile() {
  const router = useRouter();
  const username = router.query?.username as string;

  const [user, setUser] = useState<BaseUser | null>(null);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Idle);
  const [selected, setSelected] = useState<ProfileTabs>(ProfileTabs.PLATES);

  useEffect(() => {
    const load = async () => {
      setLoadingStatus(LoadingStatus.Loading);
      try {
        //@later this should be one api call
        const profile = await API.users.get(username);
        await profile.getPlates();
        setUser(profile);
        setSelected(ProfileTabs.PLATES);
        setLoadingStatus(LoadingStatus.Succeeded);
      } catch {
        setLoadingStatus(LoadingStatus.Failed);
      }
    };
    if (router.isReady) {
      load();
    }
  }, [router.isReady, username]);

  return (
    <NoSSR>
      <Head>
        <title>@{username}&apos;s Profile</title>
      </Head>
      <PageWrapper id="pageContainer">
        <LoadingWrapper status={loadingStatus} loadingType={LoadingType.Fullscreen}>
          <ScrollContainer>
            <ProfileWrapper>
              <UserInfo user={user!} />
              <ProfilePill selected={selected} setSelected={setSelected} />
              <StyledBody>
                {selected === ProfileTabs.PLATES ? (
                  <UserPlates user={user!} />
                ) : (
                  <UserList
                    users={
                      selected === ProfileTabs.FOLLOWERS ? user!.snapshot.followers : user!.snapshot.following
                    }
                  />
                )}
              </StyledBody>
            </ProfileWrapper>
          </ScrollContainer>
        </LoadingWrapper>
      </PageWrapper>
    </NoSSR>
  );
}

const StyledBody = styled.div`
  margin-top: 20px;
`;

const ProfileWrapper = styled.div`
  padding: 25px 25px 0 25px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  max-width: 990px;
`;
